import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../api.ts'
import { CATEGORIES, money } from '../format.ts'

interface Spec {
  key: string
  value: string
}

interface MachineFields {
  id: string
  model: string
  category: string
  offerTitle?: string | null
  catalogCode: string
  waproSku: string | null
  description: string | null
  techSpec: Spec[]
  retailPrice: string | null
  retailCurrency: string
  vatRate: string
  deliveryTime: string | null
  status: string
  cost?: {
    listPrice: string
    listCurrency: string
    dealerDiscountPct: string
    purchasePrice: string | null
  } | null
}

/**
 * Machine editor — owner only. Two things live here that a supplier's price
 * list never carries and that Stage 4 cannot do without: the tech spec (printed
 * on grant-application offers) and the extras list (step 2 of writing an offer
 * is "tick the extras" — with nothing to tick, the offer is a lie).
 *
 * The purchase price is deliberately NOT an input. It's computed by the
 * database from list price and dealer discount, and shown read-only. A field
 * you can type into is a field you can mistype.
 */
export function EditMachineDialog({
  machine,
  onClose,
  onSaved,
}: {
  machine: MachineFields
  onClose: () => void
  onSaved: () => void
}) {
  const [f, setF] = useState({
    model: machine.model,
    category: machine.category,
    offerTitle: machine.offerTitle ?? '',
    description: machine.description ?? '',
    retailPrice: machine.retailPrice ?? '',
    retailCurrency: machine.retailCurrency,
    vatRate: machine.vatRate,
    deliveryTime: machine.deliveryTime ?? '',
    waproSku: machine.waproSku ?? '',
    status: machine.status,
    listPrice: machine.cost?.listPrice ?? '',
    listCurrency: machine.cost?.listCurrency ?? 'PLN',
    dealerDiscountPct: machine.cost?.dealerDiscountPct ?? '0',
  })
  const [spec, setSpec] = useState<Spec[]>(machine.techSpec.length ? machine.techSpec : [{ key: '', value: '' }])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })

  // Same arithmetic the database does — shown live so the owner sees the
  // consequence of a discount before saving, not after.
  const podglądZakupu =
    f.listPrice !== '' && Number(f.listPrice) >= 0
      ? Math.round(Number(f.listPrice) * (1 - Number(f.dealerDiscountPct || 0) / 100) * 100) / 100
      : null
  const marża =
    podglądZakupu != null && f.retailPrice !== '' && f.listCurrency === f.retailCurrency
      ? Number(f.retailPrice) - podglądZakupu
      : null

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.patch(`/api/machines/${machine.id}`, {
        model: f.model,
        category: f.category,
        offerTitle: f.offerTitle || null,
        description: f.description || null,
        techSpec: spec.filter((s) => s.key.trim() || s.value.trim()),
        retailPrice: f.retailPrice === '' ? null : Number(f.retailPrice),
        retailCurrency: f.retailCurrency,
        // An emptied VAT field is "leave it", not "0 %".
        ...(f.vatRate === '' ? {} : { vatRate: Number(f.vatRate) }),
        deliveryTime: f.deliveryTime || null,
        waproSku: f.waproSku || null,
        status: f.status,
        listPrice: f.listPrice === '' ? null : Number(f.listPrice),
        listCurrency: f.listCurrency,
        dealerDiscountPct: Number(f.dealerDiscountPct || 0),
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się zapisać')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-8 pb-8 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-lg shadow-xl border border-slate-200 p-5 space-y-4 my-auto"
      >
        <div>
          <h2 className="font-bold text-slate-800">Edytuj maszynę</h2>
          <p className="text-xs text-slate-400 font-mono">{machine.catalogCode}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Model *" className="col-span-2">
            <input value={f.model} onChange={set('model')} className={INPUT} />
          </Field>
          <Field label="Status">
            <select value={f.status} onChange={set('status')} className={INPUT}>
              <option value="aktywna">Aktywna</option>
              <option value="archiwalna">Archiwalna</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Kategoria">
            <select value={f.category} onChange={set('category')} className={INPUT}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tytuł oferty" hint="na co jest oferta, np. „Rozrzutnik obornika”">
            <input value={f.offerTitle} onChange={set('offerTitle')} className={INPUT} />
          </Field>
          <Field label="Termin dostawy" hint='np. "6-8 tygodni"'>
            <input value={f.deliveryTime} onChange={set('deliveryTime')} className={INPUT} />
          </Field>
          <Field label="Indeks / SKU" hint="opcjonalny">
            <input value={f.waproSku} onChange={set('waproSku')} className={`${INPUT} font-mono`} />
          </Field>
        </div>

        <Field label="Opis">
          <textarea value={f.description} onChange={set('description')} rows={2} className={`${INPUT} resize-y`} />
        </Field>

        {/* ── Ceny ── */}
        <fieldset className="border-t border-slate-100 pt-3">
          <legend className="text-xs uppercase text-slate-400">Ceny</legend>
          <div className="grid grid-cols-4 gap-3 mt-1">
            <Field label="Cena katalogowa" hint="cennik producenta">
              <input type="number" step="0.01" min="0" value={f.listPrice} onChange={set('listPrice')} className={INPUT} />
            </Field>
            <Field label="Waluta">
              <select value={f.listCurrency} onChange={set('listCurrency')} className={INPUT}>
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
            <Field label="Rabat dealerski %" hint="ile schodzi Wam producent">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={f.dealerDiscountPct}
                onChange={set('dealerDiscountPct')}
                className={INPUT}
              />
            </Field>
            <Field label="Cena zakupu" hint="liczy program">
              <div className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm tabular text-slate-700">
                {podglądZakupu != null ? money(podglądZakupu, f.listCurrency) : '—'}
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-4 gap-3 mt-2">
            <Field label="Cena detaliczna" hint="ta trafia na ofertę">
              <input type="number" step="0.01" min="0" value={f.retailPrice} onChange={set('retailPrice')} className={INPUT} />
            </Field>
            <Field label="Waluta">
              <select value={f.retailCurrency} onChange={set('retailCurrency')} className={INPUT}>
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
            <Field label="VAT %">
              <input type="number" step="1" min="0" max="100" value={f.vatRate} onChange={set('vatRate')} className={INPUT} />
            </Field>
            <Field label="Marża">
              <div className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm tabular">
                {marża != null ? (
                  <span className={marża > 0 ? 'text-green-700' : 'text-red-700'}>{money(marża, f.retailCurrency)}</span>
                ) : (
                  // Never show 0 here: different currencies means we can't know
                  // without an FX rate, and 0 would read as "no profit".
                  <span className="text-slate-400" title="Różne waluty — marża wymaga kursu">
                    —
                  </span>
                )}
              </div>
            </Field>
          </div>
        </fieldset>

        {/* ── Specyfikacja ── */}
        <fieldset className="border-t border-slate-100 pt-3">
          <legend className="text-xs uppercase text-slate-400">Specyfikacja techniczna</legend>
          <p className="text-xs text-slate-400 mb-1.5">Trafia na ofertę — a przy ofercie do wniosku o dotację w całości.</p>
          <div className="space-y-1.5">
            {spec.map((s, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={s.key}
                  onChange={(e) => setSpec(spec.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
                  placeholder="np. Pojemność zbiornika"
                  className={`${INPUT} flex-1`}
                />
                <input
                  value={s.value}
                  onChange={(e) => setSpec(spec.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                  placeholder="np. 1000 l"
                  className={`${INPUT} flex-1`}
                />
                <button
                  type="button"
                  onClick={() => setSpec(spec.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-red-600 px-2"
                  title="Usuń"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSpec([...spec, { key: '', value: '' }])}
            className="text-xs text-brand-700 hover:underline mt-1.5"
          >
            + dodaj parametr
          </button>
        </fieldset>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-1 border-t border-slate-100">
          <button type="button" onClick={onClose} className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700">
            Anuluj
          </button>
          <button
            type="submit"
            disabled={busy || !f.model.trim()}
            className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? 'Zapisuję…' : 'Zapisz'}
          </button>
        </div>
      </form>
    </div>
  )
}

const INPUT = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-brand-600'

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string
  hint?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-400 mt-0.5">{hint}</span>}
    </label>
  )
}
